/** @jsx jsx */
import { jsx } from '@emotion/core';
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loading, useGet, usePost, MetaTag } from 'components';
import env from 'env';
import { eventFormStyle } from './eventFormStyle';

// Import colors for inline styling
const secondaryColor = '#3fae2a';

export function EventForm() {
  const { id } = useParams();
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    numOfAttending: '1',
    customFields: []
  });
  const [captchaToken, setCaptchaToken] = useState('');
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [formErrors, setFormErrors] = useState({});
  const [submitError, setSubmitError] = useState(null);
  const [showResetButton, setShowResetButton] = useState(false);

  const get = useGet();
  const post = usePost();

  useEffect(() => {
    if (id) {
      const apiUrl = env.apiBase ? `${env.apiBase}/eventapi/event/getevent/${id}` : `/eventapi/event/getevent/${id}`;
      console.log('EventForm: Calling API URL:', apiUrl);
      console.log('Event ID:', id);
      console.log('API Base:', env.apiBase);
      console.log('Full env object:', env);
      get.send(apiUrl);
    }
  }, [id]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-AU', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // Helper function to generate Outlook calendar download URL
  const getOutlookCalendarUrl = (event) => {
    const apiBase = env.apiBase || '';
    return `${apiBase}/api/calendar-download/${event.eventId}`;
  };

  // Helper function to generate Google Calendar URL
  const getGoogleCalendarUrl = (event) => {
    const eventDate = new Date(event.eventDate);
    const timeString = event.eventTime || '';
    
    // Parse start time - handle both 24-hour and 12-hour formats
    const timeParts = timeString.split(':');
    let hour = parseInt(timeParts[0] || '0');
    let minute = 0;
    
    if (timeParts.length > 1) {
      const minuteAndAmPm = timeParts[1].trim();
      const minuteParts = minuteAndAmPm.split(' ');
      minute = parseInt(minuteParts[0] || '0');
      
      // Handle AM/PM conversion (12-hour format)
      if (minuteParts.length > 1) {
        const amPm = minuteParts[1].toLowerCase();
        if (amPm === 'pm' && hour !== 12) {
          hour += 12;
        } else if (amPm === 'am' && hour === 12) {
          hour = 0;
        }
      }
      // If no AM/PM, assume it's already in 24-hour format
    }

    // Create start datetime
    const startDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), hour, minute);
    
    // Create end datetime
    let endDate;
    if (event.eventEndTime && event.eventEndTime !== '') {
      const endTimeParts = event.eventEndTime.split(':');
      let endHour = parseInt(endTimeParts[0] || '0');
      let endMinute = 0;
      
      if (endTimeParts.length > 1) {
        const endMinuteAndAmPm = endTimeParts[1].trim();
        const endMinuteParts = endMinuteAndAmPm.split(' ');
        endMinute = parseInt(endMinuteParts[0] || '0');
        
        // Handle AM/PM conversion (12-hour format)
        if (endMinuteParts.length > 1) {
          const endAmPm = endMinuteParts[1].toLowerCase();
          if (endAmPm === 'pm' && endHour !== 12) {
            endHour += 12;
          } else if (endAmPm === 'am' && endHour === 12) {
            endHour = 0;
          }
        }
        // If no AM/PM, assume it's already in 24-hour format
      }
      endDate = new Date(eventDate.getFullYear(), eventDate.getMonth(), eventDate.getDate(), endHour, endMinute);
    } else {
      // Default to 1 hour duration
      endDate = new Date(startDate.getTime() + (60 * 60 * 1000));
    }

    // Format dates for Google Calendar (YYYYMMDDTHHMMSS)
    const formatGoogleDate = (date) => {
      return date.getFullYear() +
        String(date.getMonth() + 1).padStart(2, '0') +
        String(date.getDate()).padStart(2, '0') +
        'T' +
        String(date.getHours()).padStart(2, '0') +
        String(date.getMinutes()).padStart(2, '0') +
        '00';
    };

    const startDateStr = formatGoogleDate(startDate);
    const endDateStr = formatGoogleDate(endDate);
    
    const title = encodeURIComponent(`${event.eventType} : ${event.eventTitle}`);
    const details = encodeURIComponent(event.eventShortDes || '');
    const location = encodeURIComponent(event.eventPlace || '');

    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${title}&dates=${startDateStr}/${endDateStr}&details=${details}&location=${location}&trp=true`;
  };

  const handleReset = () => {
    console.log('EventForm: Manual reset triggered');
    setSubmitError(null);
    setShowResetButton(false);
    setShowSuccessMessage(false);
    // Force reset the post hook by refreshing the page
    window.location.reload();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleCustomFieldChange = (fieldId, value) => {
    setFormData(prev => ({
      ...prev,
      customFields: prev.customFields.map(field => 
        field.customFieldId === fieldId 
          ? { ...field, customFieldValue: value }
          : field
      )
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('EventForm: Submit button clicked');
    console.log('EventForm: window.grecaptcha available?', !!window.grecaptcha);
    console.log('EventForm: env.reCaptchaSiteKey:', env.reCaptchaSiteKey);
    
    // Check if reCAPTCHA is available
    if (!window.grecaptcha) {
      console.error('EventForm: reCAPTCHA not loaded, submitting without validation');
      // Fallback: submit without reCAPTCHA
      const submitData = {
        EventId: id,
        FullName: formData.fullName,
        Email: formData.email,
        Phone: formData.phone,
        NumOfAttending: formData.numOfAttending,
        CustomFields: formData.customFields.map(field => ({
          CustomFieldId: field.customFieldId,
          CustomFieldValue: field.customFieldValue
        })),
        CaptchaResponse: ''  // Empty token as fallback
      };
      const apiUrl = env.apiBase ? `${env.apiBase}/eventapi/event/sendeventform` : `/eventapi/event/sendeventform`;
      console.log('EventForm: Fallback - Sending to URL:', apiUrl);
      console.log('EventForm: Fallback - Form data:', submitData);
      post.send(apiUrl, submitData);
      return;
    }

    // Use reCAPTCHA v3 like the contact form
    try {
      window.grecaptcha.ready(function () {
        console.log('EventForm: reCAPTCHA ready, executing...');
        window.grecaptcha.execute(env.reCaptchaSiteKey, { action: 'submit' }).then(function (token) {
          console.log('EventForm: reCAPTCHA token received:', token ? 'YES' : 'NO');
        const submitData = {
          EventId: id,
          FullName: formData.fullName,
          Email: formData.email,
          Phone: formData.phone,
          NumOfAttending: formData.numOfAttending,
          CustomFields: formData.customFields.map(field => ({
            CustomFieldId: field.customFieldId,
            CustomFieldValue: field.customFieldValue
          })),
          CaptchaResponse: token  // Use the reCAPTCHA token
        };

          const apiUrl = env.apiBase ? `${env.apiBase}/eventapi/event/sendeventform` : `/eventapi/event/sendeventform`;
          console.log('EventForm: Sending form data to URL:', apiUrl);
          console.log('EventForm: Form data:', submitData);
          console.log('EventForm: env.apiBase:', env.apiBase);
          post.send(apiUrl, submitData);
        }).catch(function(error) {
          console.error('EventForm: reCAPTCHA execute error:', error);
          // Fallback: submit without reCAPTCHA
          const submitData = {
            EventId: id,
            FullName: formData.fullName,
            Email: formData.email,
            Phone: formData.phone,
            NumOfAttending: formData.numOfAttending,
            CustomFields: formData.customFields.map(field => ({
              CustomFieldId: field.customFieldId,
              CustomFieldValue: field.customFieldValue
            })),
            CaptchaResponse: ''
          };
          const apiUrl = env.apiBase ? `${env.apiBase}/eventapi/event/sendeventform` : `/eventapi/event/sendeventform`;
      console.log('EventForm: Fallback - Sending to URL:', apiUrl);
      console.log('EventForm: Fallback - Form data:', submitData);
      post.send(apiUrl, submitData);
        });
      });
    } catch (error) {
      console.error('EventForm: reCAPTCHA error:', error);
      // Fallback: submit without reCAPTCHA
      const submitData = {
        EventId: id,
        FullName: formData.fullName,
        Email: formData.email,
        Phone: formData.phone,
        NumOfAttending: formData.numOfAttending,
        CustomFields: formData.customFields.map(field => ({
          CustomFieldId: field.customFieldId,
          CustomFieldValue: field.customFieldValue
        })),
        CaptchaResponse: ''
      };
      const apiUrl = env.apiBase ? `${env.apiBase}/eventapi/event/sendeventform` : `/eventapi/event/sendeventform`;
      console.log('EventForm: Fallback - Sending to URL:', apiUrl);
      console.log('EventForm: Fallback - Form data:', submitData);
      post.send(apiUrl, submitData);
    }
  };

  // Initialize custom fields when event data loads
  useEffect(() => {
    if (get.done() && get.response && get.response.customFields) {
      const customFields = get.response.customFields.map(field => ({
        customFieldId: field.fieldId || field.FieldId,
        customFieldName: field.fieldName || field.FieldName,
        customFieldValue: ''
      }));
      
      setFormData(prev => ({
        ...prev,
        customFields
      }));
    }
  }, [get.done(), get.response]);

  // Handle form submission response
  useEffect(() => {
    console.log('EventForm: Post status changed:', {
      loading: post.loading(),
      done: post.done(),
      hasErrors: post.hasErrors(),
      response: post.response,
      errors: post.errors,
      status: post.status
    });
    
    if (post.done()) {
      console.log('EventForm: Form submission completed');
      if (post.response === 'ok') {
        console.log('EventForm: Success response received');
        setShowSuccessMessage(true);
        setFormData({
          fullName: '', email: '', phone: '', numOfAttending: '1', customFields: [], captchaResponse: ''
        });
      } else {
        console.log('EventForm: Error response received:', post.response);
        setSubmitError(post.response || 'An error occurred during submission');
      }
    }
    
    if (post.hasErrors()) {
      console.log('EventForm: Post has errors:', post.errors);
      setSubmitError(post.errors.join(', '));
    }
  }, [post.done(), post.response, post.hasErrors(), post.errors, post.status]);

  // Add timeout mechanism to prevent infinite loading
  useEffect(() => {
    let timeoutId;
    if (post.sending() && !post.done()) {
      console.log('EventForm: Starting 30-second timeout for form submission');
      timeoutId = setTimeout(() => {
        console.error('EventForm: Form submission timed out after 30 seconds');
        setSubmitError('Form submission timed out. Please check your connection and try again.');
        setShowResetButton(true);
      }, 30000); // 30 seconds timeout
    }
    
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [post.sending(), post.done()]);

  if (get.loading()) {
    return <Loading />;
  }

  if (get.hasErrors() || !get.response || !get.response.event) {
    return (
      <div css={eventFormStyle.container}>
        <div css={eventFormStyle.error}>
          Event not found or registration is not available.
          {get.hasErrors() && (
            <div style={{ marginTop: '10px', fontSize: '14px' }}>
              Error: {get.errors.join(', ')}
            </div>
          )}
        </div>
      </div>
    );
  }

  const event = get.response.event;
  const customFields = get.response.customFields || [];
  const checkEventFull = get.response.checkEventFull;

  if (checkEventFull) {
    return (
      <div css={eventFormStyle.container}>
        <MetaTag title={`${event.eventTitle} - Fully Booked`} />
        
        {/* Event Banner */}
        {event.mailChimpImage && (
          <div css={eventFormStyle.banner}>
            <img 
              src={`/_mailchimpimages/${event.mailChimpImage}`} 
              alt={event.eventTitle}
              css={eventFormStyle.bannerImage}
            />
          </div>
        )}

        <div css={eventFormStyle.eventView}>
          <div css={eventFormStyle.event}>
            {/* Event Type Header */}
            <h4 css={eventFormStyle.eventType}>
              {event.eventSort === 'private' ? (
                <>
                  <strong style={{ color: secondaryColor }}> Private </strong>
                  {event.eventType} Registration
                </>
              ) : (
                `${event.eventType} Registration`
              )}
            </h4>
            
            {/* Event Title */}
            <h2 css={eventFormStyle.eventTitle}>{event.eventTitle}</h2>
            
            {/* Event Description */}
            <div css={eventFormStyle.eventDescription} dangerouslySetInnerHTML={{ __html: event.eventDes }} />
            
            {/* Event Details */}
            <div css={eventFormStyle.eventDetails}>
              <div css={eventFormStyle.eventDate}>
                <b>Date</b>: {new Date(event.eventDate).toLocaleDateString('en-AU', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </div>
              <div css={eventFormStyle.eventTime}>
                <b>Time</b>: {event.eventTime}
                {event.eventEndTime && event.eventEndTime !== '' && ` - ${event.eventEndTime}`}
              </div>
              <div css={eventFormStyle.eventPlace}>
                <b>Place</b>: {event.eventPlace}
              </div>
              <div style={{ marginTop: '5px' }}>
                Register to attend using the form below or call us on <a href="tel:83777795" style={{ fontWeight: 600 }}> 8377 7795 </a>.
              </div>
            </div>
          </div>
          
          <h2 css={eventFormStyle.registerHeader}>Register here :</h2>
          
          {/* Fully Booked Message - styled like old midsec */}
          <div css={eventFormStyle.fullyBookedMessage}>
            Sorry, this event is now fully booked. Please keep an eye out for future events.
          </div>
        </div>
      </div>
    );
  }

  if (showSuccessMessage) {
    return (
      <div css={eventFormStyle.container}>
        <MetaTag title={`${event.eventTitle} - Registration Successful`} />
        <div css={eventFormStyle.success}>
          <div css={{
            textAlign: 'center',
            padding: '40px 20px',
            backgroundColor: '#f8f9fa',
            borderRadius: '8px',
            boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
            maxWidth: '600px',
            margin: '0 auto'
          }}>
            <h2 css={{
              color: '#28a745',
              marginBottom: '20px',
              fontSize: '28px',
              fontWeight: 'bold'
            }}>
              THANK YOU<br />FOR REGISTERING!
            </h2>
            <p css={{
              fontSize: '16px',
              marginBottom: '30px',
              color: '#666'
            }}>
              Thank you for registering for {event.eventTitle}. You will receive a confirmation email shortly.
            </p>
            
            <p css={{
              fontSize: '16px',
              marginBottom: '20px',
              color: '#333'
            }}>
              To add this event to your calendar,<br />select the icon below.
            </p>
            
            <div css={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: '20px',
              marginTop: '20px'
            }}>
              <a 
                href={getOutlookCalendarUrl(event)} 
                target="_blank" 
                rel="noopener noreferrer"
                css={{
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <img 
                  src="/assets/social/outlook.png" 
                  alt="Add to Outlook Calendar"
                  css={{
                    width: '70px',
                    height: 'auto'
                  }}
                />
              </a>
              
              <a 
                href={getGoogleCalendarUrl(event)} 
                target="_blank" 
                rel="noopener noreferrer"
                css={{
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
              >
                <img 
                  src="/assets/social/gmail.png" 
                  alt="Add to Google Calendar"
                  css={{
                    width: '70px',
                    height: 'auto'
                  }}
                />
              </a>
            </div>
            
            <button
              css={{
                marginTop: '30px',
                padding: '12px 30px',
                backgroundColor: '#243588',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                fontSize: '16px',
                fontWeight: 'bold',
                cursor: 'pointer',
                transition: 'background-color 0.2s ease',
                '&:hover': {
                  backgroundColor: '#243588'
                }
              }}
              onClick={() => {
                setShowSuccessMessage(false);
                setFormData({
                  fullName: '', email: '', phone: '', numOfAttending: '1', customFields: []
                });
              }}
            >
              CLOSE
            </button>
          </div>
        </div>
      </div>
    );
  }

  const numOfAttendingOptions = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '10'];

  return (
    <div css={eventFormStyle.container}>
      <MetaTag title={`${event.eventTitle} - Registration`} />
      
      {/* Event Banner */}
      {event.mailChimpImage && (
        <div css={eventFormStyle.banner}>
          <img 
            src={`/_mailchimpimages/${event.mailChimpImage}`} 
            alt={event.eventTitle}
            css={eventFormStyle.bannerImage}
          />
        </div>
      )}

      <div className="container">
        <section css={eventFormStyle.eventView}>
          <div css={eventFormStyle.event}>
            {/* Event Type Header */}
            <h4 css={eventFormStyle.eventType}>
              {event.eventSort === 'private' && (
                <strong style={{ color: '#3fae2a' }}>Private </strong>
              )}
              {event.eventType} Registration
            </h4>

            {/* Event Details */}
            <h2 css={eventFormStyle.eventTitle}>{event.eventTitle}</h2>
            <div 
              css={eventFormStyle.eventDescription}
              dangerouslySetInnerHTML={{ __html: event.eventDes }}
            />
            
            <div style={{ marginTop: '20px' }}>
              <div css={eventFormStyle.eventDate}>
                <b>Date</b> : {formatDate(event.eventDate)}
              </div>
              <div css={eventFormStyle.eventTime} style={{ marginTop: '5px' }}>
                <b>Time</b> : {event.eventTime}
                {event.eventEndTime && event.eventEndTime !== '' && (
                  <span> - {event.eventEndTime}</span>
                )}
              </div>
              <div css={eventFormStyle.eventPlace} style={{ marginTop: '5px' }}>
                <b>Place</b> : {event.eventPlace}
              </div>
            </div>

            {/* Registration Form */}
            <form css={eventFormStyle.form} onSubmit={handleSubmit}>
              <div css={eventFormStyle.formGroup}>
                <label css={eventFormStyle.label}>Full Name *</label>
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleInputChange}
                  css={eventFormStyle.input}
                  required
                />
              </div>

              <div css={eventFormStyle.formGroup}>
                <label css={eventFormStyle.label}>Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  css={eventFormStyle.input}
                  required
                />
              </div>

              <div css={eventFormStyle.formGroup}>
                <label css={eventFormStyle.label}>Phone</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  css={eventFormStyle.input}
                />
              </div>

              <div css={eventFormStyle.formGroup}>
                <label css={eventFormStyle.label}>Number of Attending *</label>
                <select
                  name="numOfAttending"
                  value={formData.numOfAttending}
                  onChange={handleInputChange}
                  css={eventFormStyle.select}
                  required
                >
                  {numOfAttendingOptions.map(num => (
                    <option key={num} value={num}>{num}</option>
                  ))}
                </select>
              </div>

              {/* Custom Fields */}
              {customFields.map((field, index) => (
                <div key={field.fieldId || field.FieldId} css={eventFormStyle.formGroup}>
                  <label css={eventFormStyle.label}>
                    {field.fieldName || field.FieldName}
                  </label>
                  <input
                    type="text"
                    value={formData.customFields[index]?.customFieldValue || ''}
                    onChange={(e) => handleCustomFieldChange(
                      field.fieldId || field.FieldId, 
                      e.target.value
                    )}
                    css={eventFormStyle.input}
                  />
                </div>
              ))}

              {/* Submit Button */}
              <button
                type="submit"
                css={eventFormStyle.submitButton}
                disabled={post.sending()}
              >
                {post.sending() ? 'Submitting...' : 'Register'}
              </button>

              {/* Error Messages */}
              {post.hasErrors() && (
                <div css={eventFormStyle.errorMessage}>
                  {post.errors.join(', ')}
                </div>
              )}
              
              {post.done() && post.response !== 'ok' && (
                <div css={eventFormStyle.errorMessage}>
                  {post.response}
                </div>
              )}

              {submitError && (
                <div css={eventFormStyle.errorMessage}>
                  {submitError}
                </div>
              )}

              {showResetButton && (
                <button
                  type="button"
                  css={eventFormStyle.resetButton}
                  onClick={handleReset}
                >
                  Reset Form
                </button>
              )}
            </form>
          </div>
        </section>
      </div>
    </div>
  );
}
